import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Column, Row, Stack, useScreenSize } from "@lookiero/sty-psp-ui";
import { ToastNotificationItem } from "./ToastNotificationItem";
const NOTIFICATIONS_AUTOHIDE_TIMEOUT = 10000;
const ANIMATION_DURATION = 250;
const containerStyle = {
    position: "fixed",
    left: 0,
    top: 0,
    width: "100%",
    zIndex: 20,
    flexDirection: "column",
    pointerEvents: "none",
};
const TOAST_ITEM_STYLE = { marginTop: 16 };
const ROW_BASE_STYLE = { flex: 1, justifyContent: "center" };
const ROW_PADDED_STYLE = { flex: 1, justifyContent: "center", paddingLeft: 16, paddingRight: 16 };
const ToastNotifications = ({ domain, notifications = [], autoHideTimeout = NOTIFICATIONS_AUTOHIDE_TIMEOUT, style: customStyle, onRemove, }) => {
    const screenSize = useScreenSize();
    const isSmallDevice = screenSize === "S";
    const [renderedToasts, setRenderedToasts] = useState([]);
    const renderedToastsRef = useRef(renderedToasts);
    renderedToastsRef.current = renderedToasts;
    useEffect(() => {
        const current = renderedToastsRef.current;
        const nextMap = new Map(notifications.map((n) => [n.id, n]));
        const currentMap = new Map(current.map((t) => [t.id, t]));
        const itemsToAdd = [];
        const itemsToUpdate = [];
        const itemsToRemove = [];
        for (const [id, notification] of nextMap) {
            const existing = currentMap.get(id);
            if (!existing) {
                itemsToAdd.push({ id, notification, status: "entering", opacity: 0, translateY: -20 });
            }
            else if (existing.notification !== notification) {
                itemsToUpdate.push({ id, notification });
            }
        }
        for (const [id, item] of currentMap) {
            if (!nextMap.has(id) && item.status !== "exiting") {
                itemsToRemove.push(id);
            }
        }
        if (itemsToAdd.length || itemsToRemove.length || itemsToUpdate.length) {
            setRenderedToasts((prev) => {
                let next = prev.map((t) => {
                    const update = itemsToUpdate.find((u) => u.id === t.id);
                    if (update)
                        return { ...t, notification: update.notification };
                    if (itemsToRemove.includes(t.id))
                        return { ...t, status: "exiting", opacity: 0, translateY: -20 };
                    return t;
                });
                next = [...next, ...itemsToAdd];
                return next;
            });
            if (itemsToAdd.length) {
                // Double rAF: paint the initial state before flipping, else the CSS transition collapses into one paint.
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        setRenderedToasts((prev) => prev.map((t) => itemsToAdd.some((a) => a.id === t.id) && t.status === "entering"
                            ? { ...t, opacity: 1, translateY: 0 }
                            : t));
                    });
                });
                const timer = setTimeout(() => {
                    setRenderedToasts((prev) => prev.map((t) => itemsToAdd.some((a) => a.id === t.id) && t.status === "entering" ? { ...t, status: "stable" } : t));
                }, ANIMATION_DURATION);
                return () => clearTimeout(timer);
            }
        }
    }, [notifications]);
    const handleTransitionEnd = useCallback((id) => {
        setRenderedToasts((prev) => {
            const toast = prev.find((t) => t.id === id);
            if (toast?.status === "exiting") {
                return prev.filter((t) => t.id !== id);
            }
            return prev;
        });
    }, []);
    const rowStyle = useMemo(() => (isSmallDevice ? ROW_PADDED_STYLE : ROW_BASE_STYLE), [isSmallDevice]);
    return (React.createElement("div", { "data-testid": "toast-notifications", style: { ...containerStyle, ...customStyle } },
        React.createElement(Row, { pointerEvents: "box-none", style: rowStyle },
            React.createElement(Column, { pointerEvents: "box-none", size: { M: "1", L: "1/2" } },
                React.createElement(Stack, null, renderedToasts.map((item) => (React.createElement("div", { key: item.id, style: {
                        opacity: item.opacity,
                        transform: `translateY(${item.translateY}px)`,
                        transition: `opacity ${ANIMATION_DURATION}ms ease, transform ${ANIMATION_DURATION}ms ease`,
                        pointerEvents: item.status === "exiting" ? "none" : "auto",
                    }, onTransitionEnd: () => handleTransitionEnd(item.id) },
                    React.createElement(ToastNotificationItem, { autoHideTimeout: autoHideTimeout, domain: domain, motion: "bottom", notification: item.notification, style: TOAST_ITEM_STYLE, testID: item.id, onRemove: onRemove })))))))));
};
export { ToastNotifications };
